import React, { useState, useEffect, useCallback } from 'react';
import { RefundHistory } from './RefundHistory';
import { RevenueSummary } from './RevenueSummary';
import { Calendar, Clock, User, Phone, MapPin, CheckCircle, XCircle, Lock, LogOut, Plus, Trash2, ExternalLink, RefreshCw, Settings } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [distSettings, setDistSettings] = useState({ base_lat: 38.9087, base_lng: 1.4368, threshold_km: 17, supplement: 100 });
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, configured: false });
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [blockForm, setBlockForm] = useState({ date: '', start_time: '', end_time: '', reason: '' });
  const [manualForm, setManualForm] = useState({ patient_name: '', patient_email: '', patient_phone: '', service: 'telemedicine', patient_type: 'new', date: '', time: '', price: 150, reason: '' });

  const headers = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }), credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      setToken(data.token);
      localStorage.setItem('admin_token', data.token);
      setIsLoggedIn(true);
    } catch (e) { setLoginError(e.message); }
  };

  const logout = useCallback(() => { setToken(''); localStorage.removeItem('admin_token'); setIsLoggedIn(false); }, []);

  const fetchBookings = useCallback(async () => {
    if (tab === 'refunds' || tab === 'revenue') return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings?status=${tab}`, { headers: headers() });
      if (res.status === 401) { logout(); return; }
      setBookings(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tab, headers]);

  const fetchBlocked = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blocked-slots`, { headers: headers() });
      if (res.ok) setBlockedSlots(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => {
    if (token) {
      fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => { if (r.ok) { setIsLoggedIn(true); } else { logout(); } })
        .catch(() => logout());
    }
  }, [token]);

  useEffect(() => { if (isLoggedIn) { fetchBookings(); fetchBlocked(); fetchSettings(); fetchCalendar(); } }, [isLoggedIn, tab, fetchBookings, fetchBlocked]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/distance`, { headers: headers() });
      if (res.ok) setDistSettings(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchCalendar = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/status`, { headers: headers() });
      if (res.ok) setCalendarStatus(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveDistSettings = async () => {
    await fetch(`${API_URL}/api/admin/settings/distance`, { method: 'POST', headers: headers(), body: JSON.stringify(distSettings) });
    alert('Distance settings saved');
  };

  const connectCalendar = async () => {
    const res = await fetch(`${API_URL}/api/calendar/auth-url`, { headers: headers() });
    if (res.ok) {
      const data = await res.json();
      window.location.href = data.authorization_url;
    } else {
      const err = await res.json();
      alert(err.detail || 'Calendar not configured');
    }
  };

  const toggleDoctoralia = async (id) => {
    await fetch(`${API_URL}/api/admin/bookings/${id}/doctoralia`, { method: 'POST', headers: headers() });
    fetchBookings();
  };

  const [notice, setNotice] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const runAction = async (url, method, fallbackOk) => {
    setNotice(null);
    try {
      const res = await fetch(url, { method, headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setNotice({ type: 'error', text: 'Your session has expired. Please sign in again.' }); logout(); return; }
      if (!res.ok) { setNotice({ type: 'error', text: data.detail || `Something went wrong (error ${res.status}).` }); return; }
      setNotice({ type: 'success', text: data.message || fallbackOk });
      fetchBookings();
    } catch {
      setNotice({ type: 'error', text: 'Connection problem. Nothing was changed. Please try again.' });
    }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm('Cancel this booking and issue a full refund?')) return;
    await runAction(`${API_URL}/api/admin/bookings/${id}/cancel`, 'POST', 'Booking cancelled.');
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this unpaid booking permanently?')) return;
    await runAction(`${API_URL}/api/admin/bookings/${id}`, 'DELETE', 'Booking deleted.');
  };

  const blockSlot = async (e) => {
    e.preventDefault();
    await fetch(`${API_URL}/api/admin/block-slot`, { method: 'POST', headers: headers(), body: JSON.stringify(blockForm) });
    setShowBlockForm(false);
    setBlockForm({ date: '', start_time: '', end_time: '', reason: '' });
    fetchBlocked();
  };

  const deleteBlock = async (id) => {
    await fetch(`${API_URL}/api/admin/blocked-slots/${id}`, { method: 'DELETE', headers: headers() });
    fetchBlocked();
  };

  const createManual = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API_URL}/api/admin/manual-booking`, { method: 'POST', headers: headers(), body: JSON.stringify(manualForm) });
    const data = await res.json();
    if (data.payment_link) {
      window.prompt('Payment link to send to patient:', data.payment_link);
    }
    setShowManualForm(false);
    setManualForm({ patient_name: '', patient_email: '', patient_phone: '', service: 'telemedicine', patient_type: 'new', date: '', time: '', price: 150, reason: '' });
    fetchBookings();
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Lock size={24} className="text-[#b8860b]" />
            <h1 className="text-2xl font-semibold text-white">Admin</h1>
          </div>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            {loginError && <div data-testid="admin-login-error" className="text-red-400 text-sm text-center">{loginError}</div>}
            <input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
              className="w-full p-3 bg-[#1e293b] border border-[#334155] text-white placeholder-[#64748b] focus:outline-none focus:border-[#b8860b]" />
            <input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
              className="w-full p-3 bg-[#1e293b] border border-[#334155] text-white placeholder-[#64748b] focus:outline-none focus:border-[#b8860b]" />
            <button type="submit" className="w-full p-3 bg-[#b8860b] text-white font-medium hover:bg-[#996f0a] transition-colors" data-testid="admin-login-btn">
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#0f172a] text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-lg font-semibold">Admin Dashboard</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => { fetchBookings(); fetchBlocked(); setRefreshKey(k => k + 1); }} data-testid="admin-refresh-btn" className="text-[#94a3b8] hover:text-white"><RefreshCw size={18} /></button>
            <button onClick={logout} className="text-[#94a3b8] hover:text-white flex items-center gap-1 text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {notice && (
          <div data-testid={`admin-notice-${notice.type}`} role="alert"
            className={`mb-4 px-4 py-3 flex items-start justify-between gap-4 border text-sm ${notice.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} data-testid="admin-notice-close" className="opacity-60 hover:opacity-100">✕</button>
          </div>
        )}
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['upcoming', 'past', 'all', 'refunds', 'revenue'].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-4 py-2 text-sm font-medium transition-colors ${tab === t ? 'bg-[#0f172a] text-white' : 'bg-white border border-[#e2e8f0] text-[#475569] hover:bg-[#f1f5f9]'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
          <div className="flex-1" />
          <button onClick={() => setShowBlockForm(!showBlockForm)} className="px-4 py-2 text-sm font-medium bg-white border border-[#e2e8f0] text-[#475569] hover:bg-[#f1f5f9] flex items-center gap-1">
            <Plus size={14} /> Block Slot
          </button>
          <button onClick={() => setShowManualForm(!showManualForm)} className="px-4 py-2 text-sm font-medium bg-[#b8860b] text-white hover:bg-[#996f0a] flex items-center gap-1">
            <Plus size={14} /> Manual Booking
          </button>
          <button onClick={() => setShowSettings(!showSettings)} className="px-4 py-2 text-sm font-medium bg-white border border-[#e2e8f0] text-[#475569] hover:bg-[#f1f5f9] flex items-center gap-1">
            <Settings size={14} /> Settings
          </button>
        </div>

        {/* Block Slot Form */}
        {showBlockForm && (
          <form onSubmit={blockSlot} className="bg-white border border-[#e2e8f0] p-4 mb-6 space-y-3">
            <h3 className="font-semibold text-[#0f172a]">Block Time Slot</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input type="date" value={blockForm.date} onChange={e => setBlockForm({...blockForm, date: e.target.value})} className="p-2 border border-[#e2e8f0]" required />
              <input type="time" value={blockForm.start_time} onChange={e => setBlockForm({...blockForm, start_time: e.target.value})} className="p-2 border border-[#e2e8f0]" required />
              <input type="time" value={blockForm.end_time} onChange={e => setBlockForm({...blockForm, end_time: e.target.value})} className="p-2 border border-[#e2e8f0]" required />
              <input type="text" value={blockForm.reason} onChange={e => setBlockForm({...blockForm, reason: e.target.value})} className="p-2 border border-[#e2e8f0]" placeholder="Reason" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#0f172a] text-white text-sm">Block</button>
              <button type="button" onClick={() => setShowBlockForm(false)} className="px-4 py-2 border text-sm">Cancel</button>
            </div>
          </form>
        )}

        {/* Manual Booking Form */}
        {showManualForm && (
          <form onSubmit={createManual} className="bg-white border border-[#e2e8f0] p-4 mb-6 space-y-3">
            <h3 className="font-semibold text-[#0f172a]">Create Manual Booking</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text" value={manualForm.patient_name} onChange={e => setManualForm({...manualForm, patient_name: e.target.value})} className="p-2 border border-[#e2e8f0]" placeholder="Patient Name" required />
              <input type="email" value={manualForm.patient_email} onChange={e => setManualForm({...manualForm, patient_email: e.target.value})} className="p-2 border border-[#e2e8f0]" placeholder="Email" required />
              <input type="tel" value={manualForm.patient_phone} onChange={e => setManualForm({...manualForm, patient_phone: e.target.value})} className="p-2 border border-[#e2e8f0]" placeholder="Phone" required />
              <select value={manualForm.service} onChange={e => setManualForm({...manualForm, service: e.target.value})} className="p-2 border border-[#e2e8f0]">
                <option value="telemedicine">Telemedicine</option>
                <option value="home_visit">Home Visit</option>
              </select>
              <select value={manualForm.patient_type} onChange={e => setManualForm({...manualForm, patient_type: e.target.value})} className="p-2 border border-[#e2e8f0]">
                <option value="new">New Patient</option>
                <option value="established">Established Patient</option>
              </select>
              <input type="date" value={manualForm.date} onChange={e => setManualForm({...manualForm, date: e.target.value})} className="p-2 border border-[#e2e8f0]" required />
              <input type="time" value={manualForm.time} onChange={e => setManualForm({...manualForm, time: e.target.value})} className="p-2 border border-[#e2e8f0]" required />
              <input type="number" value={manualForm.price} onChange={e => setManualForm({...manualForm, price: Number(e.target.value)})} className="p-2 border border-[#e2e8f0]" placeholder="Price (EUR)" required />
            </div>
            <input type="text" value={manualForm.reason} onChange={e => setManualForm({...manualForm, reason: e.target.value})} className="w-full p-2 border border-[#e2e8f0]" placeholder="Reason" />
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#b8860b] text-white text-sm">Create & Get Payment Link</button>
              <button type="button" onClick={() => setShowManualForm(false)} className="px-4 py-2 border text-sm">Cancel</button>
            </div>
          </form>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white border border-[#e2e8f0] p-4 mb-6 space-y-4">
            <h3 className="font-semibold text-[#0f172a]">Settings</h3>

            <div>
              <h4 className="text-sm font-medium text-[#0f172a] mb-2">Distance Supplement</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-[#64748b]">Base Latitude</label>
                  <input type="number" step="0.0001" value={distSettings.base_lat} onChange={e => setDistSettings({...distSettings, base_lat: parseFloat(e.target.value)})} className="w-full p-2 border border-[#e2e8f0] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#64748b]">Base Longitude</label>
                  <input type="number" step="0.0001" value={distSettings.base_lng} onChange={e => setDistSettings({...distSettings, base_lng: parseFloat(e.target.value)})} className="w-full p-2 border border-[#e2e8f0] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#64748b]">Threshold (km)</label>
                  <input type="number" value={distSettings.threshold_km} onChange={e => setDistSettings({...distSettings, threshold_km: parseFloat(e.target.value)})} className="w-full p-2 border border-[#e2e8f0] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#64748b]">Supplement (€)</label>
                  <input type="number" value={distSettings.supplement} onChange={e => setDistSettings({...distSettings, supplement: parseFloat(e.target.value)})} className="w-full p-2 border border-[#e2e8f0] text-sm" />
                </div>
              </div>
              <button onClick={saveDistSettings} className="mt-2 px-4 py-2 bg-[#0f172a] text-white text-sm">Save Distance Settings</button>
            </div>

            <div>
              <h4 className="text-sm font-medium text-[#0f172a] mb-2">Google Calendar</h4>
              {calendarStatus.connected ? (
                <div className="flex items-center gap-2 text-sm text-green-700"><CheckCircle size={16} /> Connected</div>
              ) : (
                <div>
                  <p className="text-sm text-[#64748b] mb-2">{calendarStatus.configured ? 'Not connected. Click to authorize.' : 'Not configured. Set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in the backend .env'}</p>
                  {calendarStatus.configured && (
                    <button onClick={connectCalendar} className="px-4 py-2 bg-[#4285f4] text-white text-sm hover:bg-[#3367d6]">
                      Connect Google Calendar
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Blocked Slots */}
        {blockedSlots.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-[#0f172a] mb-2 text-sm">Blocked Slots</h3>
            <div className="flex flex-wrap gap-2">
              {blockedSlots.map(b => (
                <div key={b.block_id} className="flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-1 text-sm">
                  <span>{b.date} {b.start_time}–{b.end_time}</span>
                  {b.reason && <span className="text-[#64748b]">({b.reason})</span>}
                  <button onClick={() => deleteBlock(b.block_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bookings List */}
        {tab === 'revenue' ? (
          <RevenueSummary headers={headers} onUnauthorized={logout} refreshKey={refreshKey} />
        ) : tab === 'refunds' ? (
          <RefundHistory headers={headers} onUnauthorized={logout} refreshKey={refreshKey} />
        ) : loading ? (
          <div className="text-center text-[#64748b] py-12">Loading...</div>
        ) : bookings.length === 0 ? (
          <div className="text-center text-[#64748b] py-12">No bookings found.</div>
        ) : (
          <div className="space-y-3">
            {bookings.map(b => (
              <div key={b.booking_id} className="bg-white border border-[#e2e8f0] p-4" data-testid={`booking-${b.booking_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0f172a]">{b.patient_name}</span>
                      <span className={`text-xs px-2 py-0.5 ${b.patient_type === 'established' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {b.patient_type}
                      </span>
                      <span className={`text-xs px-2 py-0.5 ${b.payment_status === 'paid' ? 'bg-green-100 text-green-700' : b.payment_status === 'refunded' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {b.payment_status}
                      </span>
                      {b.status === 'cancelled' && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700">cancelled</span>}
                      {b.manual && <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700">manual</span>}
                    </div>
                    <div className="text-sm text-[#475569] mt-1">
                      <span className="capitalize">{b.service?.replace('_', ' ')}</span> — {b.date} at {b.time} — €{b.total_amount?.toFixed(0)}
                    </div>
                    {b.patient_phone && <div className="text-sm text-[#475569]">{b.patient_phone} · {b.patient_email}</div>}
                    {b.reason && <div className="text-sm text-[#64748b] mt-1">Reason: {b.reason}</div>}
                    {b.address && <div className="text-sm text-[#64748b]">Location: {b.address}</div>}
                    {b.latitude && b.longitude && (
                      <a href={`https://www.openstreetmap.org/?mlat=${b.latitude}&mlon=${b.longitude}#map=16/${b.latitude}/${b.longitude}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#b8860b] hover:underline inline-flex items-center gap-1">
                        <MapPin size={12} /> Open in Map
                      </a>
                    )}
                    {b.payment_link && (
                      <a href={b.payment_link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#b8860b] hover:underline inline-flex items-center gap-1 ml-2">
                        <ExternalLink size={12} /> Payment Link
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-[#64748b] cursor-pointer" title="Copied to Doctoralia">
                      <input type="checkbox" checked={b.copied_to_doctoralia || false} onChange={() => toggleDoctoralia(b.booking_id)} className="accent-[#b8860b]" />
                      Doctoralia
                    </label>
                    {b.status !== 'cancelled' && !['pending', 'expired'].includes(b.payment_status) && (
                      <button onClick={() => cancelBooking(b.booking_id)} data-testid={`cancel-booking-${b.booking_id}`} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" title="Cancel & Refund">
                        <XCircle size={14} /> Cancel
                      </button>
                    )}
                    {['pending', 'expired'].includes(b.payment_status) && (
                      <button onClick={() => deleteBooking(b.booking_id)} data-testid={`delete-booking-${b.booking_id}`} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" title="Delete unpaid booking">
                        <Trash2 size={14} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
